import { graphqlClient } from '@/lib/graphql-client';
import { dateOnlyOf, dateOnlyRangeClause } from '@/lib/utils/custom-date/custom-date';
import { hrTermOn } from '@/lib/utils/work-days/work-days';
import { postWorkflowWebhook, readWorkflowResponse } from '@/lib/workflow-webhook';
import { buildGetLeaveRequestsQuery } from '../graphql/queries';
import { buildGetEmployeesQuery } from '@/modules/shared/hr-lookups/graphql/queries';
import type { EmployeeOption } from '@/modules/shared/hr-lookups';
import {
  buildGetHolidaysForSheetQuery,
  buildGetHrTermsWithDetailQuery,
  buildGetLeavesForSheetQuery,
  buildGetToilsForSheetQuery,
} from '../graphql/leave-sheet-queries';
import { LeaveSheetHoliday, LeaveSheetLeave, LeaveSheetToil } from '../types/leave-sheet.types';
import {
  AllotmentBalance,
  AllotmentTerm,
  allotmentBalanceOf,
  allotmentTermOn,
} from '../utils/allotment-balance';
import { holidayDaysOf, leaveDaysOf } from '../utils/leave-days';
import { getEmployeeLeavePermissions, leaveSheetPolicyOf } from './leave-sheet.service';
import {
  BulkLeaveReviewResult,
  GetLeaveRequestsParams,
  LeaveManagerWorkflowResult,
  LeaveRequest,
  LeaveRequestArchiveWorkflowInput,
  LeaveRequestCancelWorkflowInput,
  LeaveRequestReviewWorkflowInput,
  LeaveRequestsPagingResult,
  LeaveStatus,
} from '../types/leave-manager.types';

/** Published webhook URLs of the LEAVE_REQUEST_REVIEW / LEAVE_REQUEST_ARCHIVE Blocks workflows. */
const LEAVE_REQUEST_REVIEW_WEBHOOK_URL =
  import.meta.env.VITE_LEAVE_REQUEST_REVIEW_WEBHOOK_URL ?? '';
const LEAVE_REQUEST_ARCHIVE_WEBHOOK_URL =
  import.meta.env.VITE_LEAVE_REQUEST_ARCHIVE_WEBHOOK_URL ?? '';
/** LEAVE_REQUEST_CANCEL: a manager cancels an approved leave. */
const LEAVE_REQUEST_CANCEL_WEBHOOK_URL =
  import.meta.env.VITE_LEAVE_REQUEST_CANCEL_WEBHOOK_URL ?? '';

export const getLeaveRequests = async (
  params: GetLeaveRequestsParams
): Promise<{ LeaveRequests: LeaveRequestsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query({
    query: buildGetLeaveRequestsQuery(filter, pageNo, pageSize, order),
  }) as Promise<{ LeaveRequests: LeaveRequestsPagingResult }>;
};

/**
 * Approves or rejects a leave request through the LEAVE_REQUEST_REVIEW workflow, which unconditionally
 * refuses a self-review and requires the request to still be `Pending`. A refusal comes back as
 * `IsSuccessful: false` with the workflow's message.
 */
export const reviewLeaveRequest = async (params: {
  itemId: string;
  status: Extract<LeaveStatus, 'Approved' | 'Rejected'>;
  note?: string;
}): Promise<LeaveManagerWorkflowResult> => {
  const input: LeaveRequestReviewWorkflowInput = params;
  const response = await postWorkflowWebhook(
    LEAVE_REQUEST_REVIEW_WEBHOOK_URL,
    'VITE_LEAVE_REQUEST_REVIEW_WEBHOOK_URL',
    { ...input }
  );
  const { IsSuccessful, Message } = readWorkflowResponse(response, 'LEAVE_REQUEST_REVIEW_FAILED');
  return { IsSuccessful, Message: IsSuccessful ? '' : Message };
};

/**
 * Approves or rejects several leave requests at once, each through its OWN `reviewLeaveRequest` call
 * (the same LEAVE_REQUEST_REVIEW workflow run as the single-row review dialog) — no bypass of its
 * permission check, unconditional self-review block or Pending-status check. One item's refusal
 * (e.g. it was already decided by someone else, or the workflow still refuses it for self-review)
 * never stops the others: every call is run and its own result recorded.
 */
export const bulkReviewLeaveRequests = async (params: {
  itemIds: string[];
  status: Extract<LeaveStatus, 'Approved' | 'Rejected'>;
}): Promise<BulkLeaveReviewResult> => {
  const { itemIds, status } = params;
  const settled = await Promise.allSettled(
    itemIds.map((itemId) => reviewLeaveRequest({ itemId, status }))
  );

  const result: BulkLeaveReviewResult = { succeeded: [], failed: [] };
  settled.forEach((outcome, index) => {
    const itemId = itemIds[index];
    if (outcome.status === 'fulfilled' && outcome.value.IsSuccessful) {
      result.succeeded.push(itemId);
    } else {
      result.failed.push({
        itemId,
        message: outcome.status === 'fulfilled' ? outcome.value.Message : undefined,
      });
    }
  });
  return result;
};

/** Archives a reviewed leave request through the LEAVE_REQUEST_ARCHIVE workflow. */
export const archiveLeaveRequest = async (itemId: string): Promise<LeaveManagerWorkflowResult> => {
  const input: LeaveRequestArchiveWorkflowInput = { itemId };
  const response = await postWorkflowWebhook(
    LEAVE_REQUEST_ARCHIVE_WEBHOOK_URL,
    'VITE_LEAVE_REQUEST_ARCHIVE_WEBHOOK_URL',
    { ...input }
  );
  const { IsSuccessful, Message } = readWorkflowResponse(response, 'LEAVE_REQUEST_ARCHIVE_FAILED');
  return { IsSuccessful, Message: IsSuccessful ? '' : Message };
};

/**
 * Cancels an Approved leave request of someone else through the LEAVE_REQUEST_CANCEL workflow
 * (`hrm::time-off::leave-cancel`; never the caller's own, heads only for their departments). An overtime leave
 * gets its taken hours back; the employee is told.
 */
export const cancelLeaveRequest = async (
  input: LeaveRequestCancelWorkflowInput
): Promise<LeaveManagerWorkflowResult> => {
  const response = await postWorkflowWebhook(
    LEAVE_REQUEST_CANCEL_WEBHOOK_URL,
    'VITE_LEAVE_REQUEST_CANCEL_WEBHOOK_URL',
    { ...input }
  );
  const { IsSuccessful, Message } = readWorkflowResponse(response, 'LEAVE_REQUEST_CANCEL_FAILED');
  return { IsSuccessful, Message: IsSuccessful ? '' : Message };
};

const BALANCE_PAGE_SIZE = 500;
const TERMS_PAGE_SIZE = 50;

/** A request's days counted now and what was left of its leave type before it (the review dialog). */
export interface LeaveRequestBalance {
  /** The request's working days (`leaveDaysOf`, the HR term in force on its first day). */
  days: number;
  /** `null` when the category has no limit or the employee has no term. */
  balance: AllotmentBalance | null;
}

/**
 * The request's days and what was left of its leave type before it (`allotmentBalanceOf`): the employee's HR
 * terms, the days the request counts in (`allotmentTermOn`, as the workflow picks them), the category's other
 * Pending and Approved requests in those days (each counted now in its working days inside), the holidays of
 * those days and of the request's own, and, for the default leave under the employee's TOIL rule, their
 * approved TOIL. The employee's leave rules are `heldPermissions` when given (the signed-in user's own, from
 * their token); otherwise they are read from IAM, which only admins may do.
 */
export const getLeaveRequestBalance = async (
  request: Pick<
    LeaveRequest,
    'ItemId' | 'UserId' | 'LeaveCategoryId' | 'StartDate' | 'EndDate' | 'LeaveType'
  >,
  organizationId?: string,
  heldPermissions?: readonly string[]
): Promise<LeaveRequestBalance> => {
  const terms = (
    await graphqlClient.query<{ HrProfiles: { items: AllotmentTerm[] } }>({
      query: buildGetHrTermsWithDetailQuery({ UserId: request.UserId }, TERMS_PAGE_SIZE),
    })
  ).HrProfiles.items;
  const startDay = dateOnlyOf(request.StartDate);
  const endDay = dateOnlyOf(request.EndDate) || startDay;
  const window = allotmentTermOn(terms, startDay);
  const range = window.term ? dateOnlyRangeClause(window.start, window.end) : null;
  const holidayFrom = range && window.start < startDay ? window.start : startDay;
  const holidayTo = range && window.end > endDay ? window.end : endDay;
  const holidayRange = dateOnlyRangeClause(holidayFrom, holidayTo);
  const [usage, holidays, permissions] = await Promise.all([
    range
      ? graphqlClient.query<{ LeaveRequests: { items: LeaveSheetLeave[] } }>({
          query: buildGetLeavesForSheetQuery(
            {
              UserId: request.UserId,
              LeaveCategoryId: request.LeaveCategoryId,
              ItemId: { $ne: request.ItemId },
              Status: { $in: ['Approved', 'Pending'] },
              StartDate: { $lt: range.$lt },
              EndDate: { $gte: range.$gte },
            },
            BALANCE_PAGE_SIZE
          ),
        })
      : { LeaveRequests: { items: [] as LeaveSheetLeave[] } },
    holidayRange
      ? graphqlClient.query<{ Holidays: { items: LeaveSheetHoliday[] } }>({
          query: buildGetHolidaysForSheetQuery({ StartDate: holidayRange }, BALANCE_PAGE_SIZE),
        })
      : { Holidays: { items: [] as LeaveSheetHoliday[] } },
    !range
      ? ([] as string[])
      : heldPermissions
        ? [...heldPermissions]
        : getEmployeeLeavePermissions(request.UserId, organizationId),
  ]);
  const days = leaveDaysOf(
    request,
    hrTermOn(terms, startDay)?.WorkDays ?? [],
    holidayDaysOf(holidays.Holidays.items)
  );
  if (!range) return { days, balance: null };
  const policy = leaveSheetPolicyOf((permission) => permissions.includes(permission));
  const toils = policy.toil
    ? (
        await graphqlClient.query<{ ToilRequests: { items: LeaveSheetToil[] } }>({
          query: buildGetToilsForSheetQuery(
            { UserId: request.UserId, Status: 'Approved', ToilDate: range },
            BALANCE_PAGE_SIZE
          ),
        })
      ).ToilRequests.items
    : [];
  const balance = allotmentBalanceOf({
    window,
    categoryId: request.LeaveCategoryId,
    usage: usage.LeaveRequests.items,
    toils,
    policy,
    lastDay: endDay,
    holidays: holidays.Holidays.items,
  });
  return { days, balance };
};

const EMPLOYEE_SEARCH_PAGE_SIZE = 20;

/**
 * Employees matching a name fragment within the user's department scope (`departmentCondition`, from
 * `useDepartmentScope().getDepartmentIdCondition`; `undefined` = every department), newest first like
 * the shared employee search.
 */
export const searchScopedEmployees = async (
  keyword: string,
  departmentCondition: string | { $in: string[] } | undefined
): Promise<EmployeeOption[]> => {
  if (typeof departmentCondition === 'object' && departmentCondition.$in.length === 0) return [];
  const filter = {
    ...(keyword ? { DisplayName: { $regex: keyword, $options: 'i' } } : {}),
    ...(departmentCondition ? { DepartmentId: departmentCondition } : {}),
  };
  const result = await graphqlClient.query<{ Employees: { items: EmployeeOption[] } }>({
    query: buildGetEmployeesQuery(filter, 1, EMPLOYEE_SEARCH_PAGE_SIZE, [
      { field: 'CreatedDate', direction: 'DESC' },
    ]),
  });
  return result.Employees.items;
};
